import axios from 'axios';
import { grpc } from '@improbable-eng/grpc-web';
import { makeWAVHeader, StreamingSpeechHandler } from './streamingSpeech';
import { StreamingRecognizeResponse } from '@gen/proto/lr/speech/v2beta1/speech_pb';
import transcript_pb from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import { durationToFloat } from './helpers';
import { LanguageCodeOption } from './speech';

import { WordInfo } from './models';
import { SPEECH_API_KEY, SPEECH_RTG_SERVICE_HOST, SPEECH_SERVICE_HOST } from './config';

interface Options {
  onAudioSrcUpdated: (audioURL: string) => void;
  onError: (error: string) => void;
  onHypothesis: (hypothesis: string) => void;
  onResults: (results: WordInfo[]) => void;
}

class SpeechEditorWrapper {
  originalTranscript = new transcript_pb.Transcript();
  private speech!: StreamingSpeechHandler;
  private onAudioSrcUpdated: (audioURL: string) => void;
  private onError: (error: string) => void;
  private onHypothesis: (hypothesis: string) => void;
  private onResults: (results: WordInfo[]) => void;
  private chunks: ArrayBuffer[] = [];

  constructor(options: Options) {
    this.onAudioSrcUpdated = options.onAudioSrcUpdated;
    this.onError = options.onError;
    this.onHypothesis = options.onHypothesis;
    this.onResults = options.onResults;
  }

  private onMessage = (res: StreamingRecognizeResponse) => {
    if (res.getResultsList().length > 0) {
      const result = res.getResultsList()[0];
      if (!result.getIsFinal()) {
        const currentHypothesis = result.getAlternativesList()[0].getTranscript();

        this.onHypothesis(currentHypothesis);
      } else {
        const wordInfos: WordInfo[] = res
          .getResultsList()[0]
          .getAlternativesList()[0]
          .getWordsList()
          .filter((wordInfo) => wordInfo.getWord().length > 0)
          .map((wordInfo) => {
            const startTime = durationToFloat(wordInfo.getStartTime());
            const endTime = durationToFloat(wordInfo.getEndTime());
            return {
              word: wordInfo.getWord() + (wordInfo.getWord().endsWith('\n') ? '' : ' '),
              startTime,
              endTime,
              id: `w_${startTime}_${endTime}`,
            };
          });

        const originalTranscript = this.originalTranscript;
        const segment = originalTranscript.addSegments();
        const wordList = res.getResultsList()[0].getAlternativesList()[0].getWordsList();
        wordList.forEach((word) => {
          const tWord = new transcript_pb.Transcript.TranscriptSegment.WordInfo();
          tWord.setWord(word.getWord() + ' ');
          tWord.setEndTime(word.getEndTime());
          tWord.setStartTime(word.getStartTime());
          segment.addWords(tWord);
        });
        segment.setEndTime(wordList[wordList.length - 1].getEndTime());
        segment.setStartTime(wordList[0].getStartTime());

        this.originalTranscript = originalTranscript;

        if (wordInfos.length > 0) {
          this.onResults(wordInfos);
        }
      }
    }
  };

  fetchAudioArrayBuffer = async (url: string): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      return axios({
        url,
        method: 'GET',
        responseType: 'arraybuffer',
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  getOriginalChunks = async (url: string): Promise<void> => {
    try {
      const arrayBuffer = await this.fetchAudioArrayBuffer(url);
      this.chunks = [arrayBuffer];
    } catch (error) {
      console.debug('error decoding, ', error);
      throw error;
    }
  };

  start = async (languageCode: LanguageCodeOption['value']): Promise<void> => {
    this.originalTranscript = new transcript_pb.Transcript();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      const speechServerUrl =
        languageCode && (languageCode.startsWith('is-IS-x-rtg-') || languageCode === 'is-IS-x-radiology')
          ? SPEECH_RTG_SERVICE_HOST
          : SPEECH_SERVICE_HOST;

      this.speech = new StreamingSpeechHandler({
        stream,
        languageCode,
        onMessage: this.onMessage,
        onEnd: (status: grpc.Code, message: string) => {
          if (status !== grpc.Code.OK) {
            this.onError('Tenging við þjón mistókst. Prófaðu að endurglæða síðuna og reyna aftur.');
          }
        },
        onAudioSrcUpdated: this.onAudioSrcUpdated,
        originalChunks: this.chunks,
        serverUrl: speechServerUrl,
        serverKey: SPEECH_API_KEY,
      });
      return Promise.resolve();
    } catch (error) {
      return Promise.reject('Gat ekki hafið upptöku. Prófaðu að endurglæða síðuna og reyna aftur.');
    }
  };

  getRecording = async () => {
    if (!this.speech) {
      return Promise.reject();
    }
    const chunks = this.speech.chunks;
    const header = makeWAVHeader(chunks.reduce((cum, val) => cum + val.byteLength, 0) / 2, {
      sampleRate: this.speech.audioContext.sampleRate,
    });
    return Promise.resolve({ chunks, header });
  };

  pause = () => {
    if (!this.speech) throw new Error('tiro/no-speech');
    this.speech.pause();
  };

  resume = () => {
    if (!this.speech) throw new Error('tiro/no-speech');
    this.speech.resume();
  };

  stop = () => {
    if (!this.speech) {
      return;
    }
    if (!this.speech.closed) {
      this.speech.close();
    }
  };

  close = () => {
    if (this.speech && !this.speech.closed) {
      this.speech.close();
    }
  };
}

export default SpeechEditorWrapper;
